import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/root/auth.service';
import { DriversService } from 'src/app/root/drivers.service';
import { EldService } from 'src/app/root/eld.service';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import { ELDGroup, UserInvite } from 'src/app/types';

interface FormGroupValue {
  name: string;
  phoneNumber: string;
  email: string;
  isAdmin: boolean;
  isDriver: boolean;
  password: string;
  cut: number;
  cdl: string;
  eldGroupId: number;
}

@Component({
  selector: 'lohi-invite-driver',
  templateUrl: './invite-driver.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteDriverComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public eldGroups$: Observable<ELDGroup[]>;
  public companyName$: Observable<string>;
  public formGroup: FormGroup;

  constructor(
    private fb: FormBuilder,
    private driversService: DriversService,
    private snackBar: SnackBarService,
    private router: Router,
    private eldService: EldService,
    private authService: AuthService,
  ) {}

  public ngOnInit(): void {
    this.eldGroups$ = this.eldService.groupDrivers$.pipe(
      map((response) => {
        return response ? response.groups : [];
      }),
    );
    this.companyName$ = this.authService.userInfo$.pipe(map((ui) => ui?.companyName));
    this.formGroup = this.fb.group({
      name: [null, [Validators.required]],
      phoneNumber: [null, [Validators.required]],
      email: [null, [Validators.required]],
      isAdmin: [false, [Validators.required]],
      isDriver: [true, [Validators.required]],
      password: [null, [Validators.required]],
      cut: [null, [Validators.required]],
      cdl: [null, [Validators.required]],
      eldGroupId: [null],
    });

    combineLatest([
      this.formGroup.get('isAdmin').valueChanges.pipe(startWith(this.formGroup.get('isAdmin').value)),
      this.formGroup.get('isDriver').valueChanges.pipe(startWith(this.formGroup.get('isDriver').value)),
    ])
      .pipe(takeUntil(this.destroy$$))
      .subscribe(([isAdmin, isDriver]) => {
        if (isAdmin && !isDriver) {
          this.formGroup.get('cut').disable();
          this.formGroup.get('cdl').disable();
          this.formGroup.get('eldGroupId').disable();
        } else {
          this.formGroup.get('cut').enable();
          this.formGroup.get('cdl').enable();
          this.formGroup.get('eldGroupId').enable();
        }
      });
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public async invite() {
    if (this.formGroup.invalid) {
      return;
    }

    const value: FormGroupValue = this.formGroup.value;
    const userInvite: UserInvite = {
      id: null,
      name: value.name,
      phoneNumber: value.phoneNumber,
      email: value.email,
      permissions: [],
      password: value.password,
      cut: value.cut,
      cdl: value.cdl,
      eldGroupId: value.eldGroupId,
    };

    if (value.isDriver) {
      userInvite.permissions.push('driver');
    }
    if (value.isAdmin) {
      userInvite.permissions.push('admin');
    }

    const success = await this.driversService.inviteUser(userInvite);
    if (!success) {
      return;
    }

    this.snackBar.showMessage('User Invited!');
    this.router.navigate(['/', 'drivers']);
  }
}
